<template>
  <InnerView>
    <template #header>
      <div class="transition-height" :class="welcomeBack ? 'h-[72px]' : 'h-[42px]'">
        <!-- WELCOME BACK -->
        <Transition name="fade" mode="out-in">
          <div v-if="welcomeBack" class="text-primary-contrast font-bold px-1 space-y-1 pt-1 pb-2">
            <h2 class="text-3xl">{{ $t('home.welcome-back.title') }}</h2>
            <h3 class="text-lg">
              {{ $t('home.welcome-back.username', { username }) }}
            </h3>
          </div>
          <div
            v-else-if="!isLoggedIn"
            class="text-primary-contrast font-bold px-1 pb-2 transition-all flex gap-2 items-center"
          >
            <PreviousButton v-if="pathLength" @click="goBack" :label="$t('modal.back')" />
            <button class="text-3xl" @click="goToHome()">
              {{ $t('home.title') }}
            </button>
          </div>
        </Transition>
      </div>
      <div class="flex flex-col gap-3">
        <urgent-notifications-view
          v-if="urgentNotifications.length"
          class="urgent-notifications"
          :notifications="urgentNotifications"
          :context="{ editor: editorCtx }"
          @reach-content="handleReachContent"
          @click:inline-content="
            goToEntity({
              ...$event,
              source: 'inline',
              deepLoading: true,
              fromToast: true
            })
          "
          @click:inline-content-modal="
            goToEntity({
              ...$event,
              source: 'inline',
              forceModal: true,
              deepLoading: true,
              fromToast: true
            })
          "
        />
        <Toast parentWrapper="header" :is-open="true" />
        <CopilotToast v-if="displayCopilotToast" />
      </div>
    </template>
    <div class="bg-gray-1 pt-9 px-4 pb-4 h-full w-full overflow-y-auto">
      <router-view />
    </div>
  </InnerView>
</template>

<script setup lang="ts">
import InnerView from '@commons/components/Layout/InnerView.vue'
import Toast from '@commons/components/Layout/Toast/Toast.vue'
import {
  useAdminStore,
  useNotificationStore,
  useKnowledgeStore,
  useCopilotStore
} from '@getmayday/vue-modules'
import useEntities from '@commons/composables/useEntities'
import PreviousButton from '@commons/components/Library/PreviousButton.vue'
import CopilotToast from '@commons/components/Copilot/CopilotToast.vue'

const adminStore = useAdminStore()
const { username } = storeToRefs(adminStore)

const welcomeBack = ref(false)
const notificationStore = useNotificationStore()
const { urgentNotifications } = storeToRefs(notificationStore)

const knowledgeStore = useKnowledgeStore()
const { path } = storeToRefs(knowledgeStore)

const copilotStore = useCopilotStore()
const { copilotLoading, copilotHasData } = storeToRefs(copilotStore)

const router = useRouter()

const { goToEntity, goBack } = useEntities()

const handleFetchInlineContent = async (id: string, lang: string, type: string) => {
  if (type === 'Case') {
    return knowledgeStore.getCase(id, lang)
  }
  return knowledgeStore.getContent(id, lang)
}

const editorCtx = {
  handleFetchInlineContent,
  handleFetchInlineMention: adminStore.handleFetchInlineMention
}

const handleReachContent = ({
  id,
  type,
  source,
  lang
}: {
  id: string
  type: string
  source: string
  lang: string
}) => {
  goToEntity({ id, type, deepLoading: true, source, lang })
}

const pathLength = computed(() => path.value.length > 1)

const displayCopilotToast = computed(() => {
  return copilotHasData.value || copilotLoading.value
})

const goToHome = () => {
  knowledgeStore.resetPath()
  router.push({ path: '/', query: { force: 1 } })
}
</script>
